import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  MerchantCreativeRunView,
  MerchantProfileView,
} from '../../merchant/api.js';
import type { Journey } from './productionLineJourney.js';

export const MILESTONES = [
  'business',
  'research',
  'idea',
  'production',
  'video',
] as const;
export type Milestone = (typeof MILESTONES)[number];

export function currentMilestone(
  view: MerchantProfileView | null,
  run: MerchantCreativeRunView | null,
  journey: Journey,
): Milestone | null {
  if (view === null) return null;
  if (view.profile?.reviewState !== 'APPROVED') return 'business';
  if (view.analysis.progress.overallStatus !== 'completed') return 'research';
  if (journey === 'recommendations' || run === null) return 'idea';
  if (run.video !== null || run.merchantState === 'READY') return 'video';
  return run.confirmedAt !== null ? 'production' : 'idea';
}

export function milestoneParam(value: string | null): Milestone | null {
  return MILESTONES.find((step) => step === value) ?? null;
}

export const milestoneCopy = {
  en: {
    label: 'Project journey',
    business: 'Business',
    research: 'Research',
    idea: 'Idea & script',
    production: 'Production',
    video: 'Your video',
    done: 'Complete',
    current: 'Current step',
    next: 'Upcoming',
    viewing:
      'You are viewing another step. This does not restart or change the current work.',
    back: 'Return to current step',
    unavailable: 'This step is not available yet.',
  },
  ar: {
    label: 'مراحل المشروع',
    business: 'النشاط',
    research: 'البحث',
    idea: 'الفكرة والنص',
    production: 'الإنتاج',
    video: 'الفيديو',
    done: 'مكتمل',
    current: 'الخطوة الحالية',
    next: 'لاحقاً',
    viewing: 'أنت تعرض خطوة أخرى. العرض لا يعيد تشغيل العمل ولا يغيّر حالته.',
    back: 'العودة للخطوة الحالية',
    unavailable: 'هذه الخطوة غير متاحة بعد.',
  },
} as const;

export function ProductionLineMilestones({
  current,
  viewed,
  lineId,
  language,
  versionId,
}: {
  readonly current: Milestone | null;
  readonly viewed: Milestone | null;
  readonly lineId: string;
  readonly language: 'en' | 'ar';
  readonly versionId?: string;
}): ReactNode {
  const copy = milestoneCopy[language];
  const currentIndex = current === null ? -1 : MILESTONES.indexOf(current);
  const base = `/production-lines/${encodeURIComponent(lineId)}`;
  return (
    <>
      <nav aria-label={copy.label}>
        <ol className="mj-ws-milestones">
          {MILESTONES.map((step, index) => {
            const enabled = index <= currentIndex;
            const done =
              index < currentIndex || (current === 'video' && step === 'video');
            const content = (
              <>
                <span className="mj-ws-milestone-mark" aria-hidden="true">
                  {done ? '✓' : index + 1}
                </span>
                <span>
                  <span>{copy[step]}</span>{' '}
                  <small>
                    {done ? copy.done : step === current ? copy.current : copy.next}
                  </small>
                </span>
              </>
            );
            return (
              <li
                key={step}
                data-done={done}
                data-current={step === current}
                data-viewed={step === viewed}
              >
                {enabled ? (
                  <Link
                    to={`${base}?step=${step}${versionId ? `&version=${encodeURIComponent(versionId)}` : ''}`}
                    aria-current={step === current ? 'step' : undefined}
                  >
                    {content}
                  </Link>
                ) : (
                  <span aria-disabled="true">{content}</span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      {viewed !== null && current !== null && viewed !== current ? (
        <div className="mj-ws-step-note">
          <span>{copy.viewing}</span>
          <Link
            to={versionId ? `${base}?version=${encodeURIComponent(versionId)}` : base}
          >
            {copy.back}
          </Link>
        </div>
      ) : null}
    </>
  );
}
